import React, { useState, useMemo, useRef } from 'react'
import SVG from 'react-inlinesvg'
import moment from 'moment'
import { groupBy } from 'lodash'
import {
  navigateTo,
  map,
  isBlank,
  useModal,
  useOutsideClick,
  varClass,
  useDatePickerState,
  Load,
  useApiPost,
  IInsightsV1Statistics,
} from '../shared'
import { BoostModal, ConfirmModal } from './Modals'
import { CampaignsFilter, departments } from './shared'
import { getCurrentCurrency, postRequest } from '../../services'
import ReactTooltip from 'react-tooltip'

const getFilterValues = (
  filters: { key: string; value: string }[],
  key: string
): Set<string> | null => {
  const values = filters.find((filter) => filter.key === key)?.value
  if (!values) return null
  const filteredValues = values
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value)
  if (filteredValues.length) {
    return new Set(filteredValues)
  } else {
    return null
  }
}

export const CampaignBoosts = (props) => {
  const {
    advertiserUuid,
    data,
    channels,
    channelsMap,
    reloadBoosts,
    showModal,
  } = props

  const [filters, setFilters] = useState([
    {
      key: 'channelUuids',
      value: '',
      label: '',
    },
    {
      key: 'departments',
      value: '',
      label: '',
    },
  ])

  const filterReset = filters
    .filter((filter) => filter.value)
    .map((filter) => filter.value)
    .join(',')

  const shownBoosts = useMemo(() => {
    if (!isBlank(data)) {
      const channelUuids = getFilterValues(filters, 'channelUuids')
      const departments = getFilterValues(filters, 'departments')
      return data.filter((boost) => {
        if (channelUuids && !channelUuids.has(boost.boostChannelUuid))
          return false
        if (departments && !departments.has(boost.boostDepartment)) return false
        return true
      })
    } else {
      return []
    }
  }, [filterReset])

  const groupedBoosts = useMemo(() => {
    const grouped = groupBy(shownBoosts, (boost) => [
      boost.boostName,
      boost.boostDepartment,
      boost.boostFromPercentage,
      boost.boostFromFlat,
      boost.boostToPercentage,
      boost.boostToFlat,
      boost.boostStartDate,
      boost.boostEndDate,
    ])
    return Object.keys(grouped)
      .map((key) => {
        const boostUuids = grouped[key].map((item) => item.boostUuid).flat()
        const boostChannelUuids = grouped[key].map(
          (item) => item.boostChannelUuid
        )
        return {
          boostAccountUuid: grouped[key][0].boostAccountUuid,
          boostAdvertiserUuid: grouped[key][0].boostAdvertiserUuid,
          boostName: grouped[key][0].boostName,
          boostDepartment: grouped[key][0].boostDepartment,
          boostFromFlat: grouped[key][0].boostFromFlat,
          boostFromPercentage: grouped[key][0].boostFromPercentage,
          boostToFlat: grouped[key][0].boostToFlat,
          boostToPercentage: grouped[key][0].boostToPercentage,
          boostDiffValue: grouped[key][0].boostDiffValue,
          boostStartDate: grouped[key][0].boostStartDate,
          boostEndDate: grouped[key][0].boostEndDate,
          boostCreatedDatetime: grouped[key][0].boostCreatedDatetime,
          boostUpdatedDatetime: grouped[key][0].boostUpdatedDatetime,
          boostUuids,
          boostChannelUuids,
        }
      })
      .filter((v) => v)
  }, [shownBoosts])

  return (
    <div>
      <div className="row row-center row-space-between m-b-7">
        <div className="vertical-middle">
          <CampaignsFilter
            {...{ filters, setFilters, channels, departments }}
          />
        </div>
        <div>
          <button
            className="btn btn-dark btn-md"
            onClick={() => {
              showModal(
                <BoostModal
                  advertiserUuid={advertiserUuid}
                  channels={channels}
                  departments={departments}
                  onDone={reloadBoosts}
                />,
                {
                  cardClassName: 'advertiser-boost-modal',
                }
              )
            }}
          >
            Create new
          </button>
        </div>
      </div>

      <div className="text-dark text-bold m-t-30 m-b-5">Campaign boosts</div>
      <div className="card-body-fill">
        <div className="table-container-overflow">
          <table className="table table-bodered table-sortable advertiser-campaigns-table">
            <thead className="table-sticky">
              <tr>
                <th>Name</th>
                <th>Sites</th>
                <th>Boost</th>
                <th className="text-nowrap">
                  <span>Added value</span>
                  <span
                    data-tip="Commission added by campaign boost & approx. total commission without boost"
                    data-for="boost-tooltip"
                    className="m-x-1 relative-top-1"
                  >
                    <SVG src="/images/recommendations/icon-info.svg" />
                  </span>
                </th>
                <th>Dates</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!isBlank(shownBoosts) ? (
                <>
                  {map(groupedBoosts, (boost, index) => (
                    <CampaignBoostItem
                      key={boost.boostUuids.join(',')}
                      boost={boost}
                      isLast={index === groupedBoosts.length - 1}
                      {...{
                        advertiserUuid,
                        channels,
                        channelsMap,
                        reloadBoosts,
                        showModal,
                      }}
                    />
                  ))}
                </>
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-light text-bolder"
                  >
                    Nothing found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ReactTooltip
          id="boost-tooltip"
          type="dark"
          effect="solid"
          place="bottom"
        />
      </div>
    </div>
  )
}

const CampaignBoostItem = (props) => {
  const {
    advertiserUuid,
    channels,
    boost,
    channelsMap,
    reloadBoosts,
    showModal,
    isLast,
  } = props

  const datePickerState = useDatePickerState()

  const [showAllChannels, setShowAllChannels] = useState(false)
  const shownChannelUuids = showAllChannels
    ? boost.boostChannelUuids
    : boost.boostChannelUuids.slice(0, 3)

  return (
    <tr key={boost.boostUuid}>
      <td>
        <div className="row row-center row-space-between row-nowrap gap-4">
          <div>
            {boost.boostName}
            <br />
            <small>{boost.boostDepartment}</small>
          </div>
          <button
            className="link link-lighter"
            onClick={async (event) => {
              datePickerState.setRange({
                ...datePickerState.range,
                startDate: moment(boost.boostStartDate).startOf('day').toDate(),
                endDate: moment(boost.boostEndDate).endOf('day').toDate(),
              })
              navigateTo(event, `/insights/reports?tab=boost`)
            }}
          >
            <SVG src="/images/icon-details.svg" className="vertical-middle" />
          </button>
        </div>
      </td>
      <td>
        {shownChannelUuids.map((boostChannelUuid: string) => (
          <div key={boostChannelUuid} className="text-nowrap">
            <span
              className="badge badge-hoverable text-smaller text-nowrap m-05"
              onClick={(event) => {
                if (channelsMap[boostChannelUuid]) {
                  navigateTo(event, `/sites/${boostChannelUuid}`)
                }
              }}
            >
              {channelsMap[boostChannelUuid]
                ? channelsMap[boostChannelUuid].channelName || 'Unnamed channel'
                : 'Deleted channel'}
            </span>
          </div>
        ))}
        {boost.boostChannelUuids.length > 3 && (
          <div>
            <span
              className="badge badge-hoverable text-smaller text-nowrap m-05"
              onClick={() => setShowAllChannels(!showAllChannels)}
            >
              {showAllChannels ? 'Show less' : 'Show all sites'}
            </span>
          </div>
        )}
      </td>
      <td>
        <CampaignBoostChange boost={boost} />
      </td>
      <td>
        <CampaignBoostAddedValue boost={boost} />
      </td>
      <td>
        <div className="text-nowrap">
          {moment(boost.boostStartDate).format('YYYY-MM-DD')} —{' '}
          {moment(boost.boostEndDate).format('YYYY-MM-DD')}
        </div>
      </td>
      <td className="text-right">
        <CampaignBoostActionMenu
          boosts={boost}
          isLast={isLast}
          {...{ advertiserUuid, channels, showModal, reloadBoosts }}
        />
      </td>
    </tr>
  )
}

const CampaignBoostChange = (props) => {
  const { boost } = props

  const isPercent = !!boost.boostFromPercentage
  const fromValue = isPercent ? boost.boostFromPercentage : boost.boostFromFlat
  const toValue = isPercent ? boost.boostToPercentage : boost.boostToFlat
  const fromValueStr = `${fromValue}${isPercent ? '%' : ''}`
  const toValueStr = `${toValue}${isPercent ? '%' : ''}`
  const diffValue = ((toValue - fromValue) / (fromValue || 1)) * 100
  const diffValueStr = `${diffValue > 0 ? '+' : ''}${
    diffValue === Math.round(diffValue) ? diffValue : diffValue.toFixed(2)
  }%`

  return (
    <div className="text-center">
      <div className="vertical-middle">
        <span>{fromValueStr}</span>
        <SVG src="/images/overview2/icon-arrow.svg" className="m-x-05" />
        <span>{toValueStr}</span>
      </div>
      <div className="m-t-1 o-80">{diffValueStr}</div>
    </div>
  )
}

const CampaignBoostAddedValue = (props) => {
  const { boost } = props

  const currencyCode = getCurrentCurrency()
  const data = useApiPost<IInsightsV1Statistics>('/insights/v1/statistics', {
    groupBy: 'name',
    fromDate: moment(boost.boostStartDate).toDate(),
    toDate: moment(boost.boostEndDate).toDate(),
    filters: {
      channelUuids: boost.boostChannelUuids,
      advertiserUuids: [boost.boostAdvertiserUuid],
      boostDepartment: [boost.boostDepartment],
    },
  })

  if (!data) {
    return <Load />
  }

  const { conversionTotalCommission = 0, conversionUnboostedCommission = 0 } =
    data?.rows?.[0] || {}
  if (!conversionUnboostedCommission) {
    return <div className="text-center text-bold o-50">—</div>
  }

  return (
    <div className="column column-right column-fill">
      <div className="value-badge value-badge-conversionCommission text-nowrap">
        {`${(conversionTotalCommission - conversionUnboostedCommission).toFixed(
          2
        )} ${currencyCode}`}
      </div>
      <div className="row row-center row-end row-nowrap m-t-1">
        <div className="value-badge value-badge-conversionCommission value-badge-sm text-nowrap">
          {`${conversionUnboostedCommission} ${currencyCode}`}
        </div>
      </div>
    </div>
  )
}

const CampaignBoostActionMenu = (props) => {
  const { advertiserUuid, channels, boosts, isLast, reloadBoosts } = props

  const { showModal, hideModal } = useModal()

  const ref = useRef<HTMLDivElement>()
  const [open, setOpen] = useState(false)
  useOutsideClick(ref, () => {
    setOpen(false)
  })

  const handleEdit = () => {
    showModal(
      <BoostModal
        advertiserUuid={advertiserUuid}
        boosts={boosts}
        channels={channels}
        departments={departments}
        onDone={reloadBoosts}
      />,
      {
        cardClassName: 'advertiser-boost-modal',
      }
    )
  }

  const handleDelete = () => {
    showModal(
      <ConfirmModal
        title="Remove campaign boost"
        desc={`Are you sure you want to remove this campaign boost?`}
        hideModal={hideModal}
        handleAgree={async () => {
          await postRequest(`/advs/v1/boosts/${advertiserUuid}/delete`, {
            boostUuids: boosts.boostUuids,
          })
          reloadBoosts()
        }}
      />
    )
  }

  return (
    <div
      className={varClass({
        'table-menu relative': true,
        'table-menu-last': isLast,
        'open': open,
      })}
      ref={ref}
    >
      <button className="link" onClick={() => setOpen(!open)}>
        <SVG src="/images/advertisers/icon-dots.svg" />
      </button>
      <ul>
        <li>
          <button
            className="link"
            onClick={() => {
              setOpen(false)
              handleEdit()
            }}
          >
            Edit
          </button>
        </li>
        <li>
          <button
            className="link"
            onClick={() => {
              setOpen(false)
              handleDelete()
            }}
          >
            Remove
          </button>
        </li>
      </ul>
    </div>
  )
}
